import { Icon } from "@/components/icons/Icon";
import { AmplitudeEvent, useAmplitudeOnClick } from "@/utils/amplitude";
import { classNames } from "@/utils/classnames";
import React, { ForwardedRef, forwardRef } from "react";

// ------------------------------------------------------------------
// CheckboxV2 — 16×16 checkbox with selected / unselected /
// indeterminate states. Presentational: renders a
// <button role="checkbox"> so keyboard + click work out of the box
// without pulling in @tailwindcss/forms' styling on a real
// <input type="checkbox">.
//
// Callers own the state:
//   checked: true / false
//   indeterminate: true → renders the dash glyph, ignores `checked`
//   onChange?(next: boolean) fires on click
// ------------------------------------------------------------------

export const CheckboxV2Sizes = ["md"] as const;
export type CheckboxV2Size = (typeof CheckboxV2Sizes)[number];

export interface CheckboxV2Props
  extends Omit<React.ComponentProps<"button">, "ref" | "children" | "onChange"> {
  "data-testid"?: string;
  size?: CheckboxV2Size;
  checked?: boolean;
  indeterminate?: boolean;
  onChange?: (checked: boolean) => void;
}

export interface CheckboxV2ControlProps {
  checked?: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  className?: string;
}

export const CheckboxV2Control: React.FC<CheckboxV2ControlProps> = ({
  checked = false,
  indeterminate = false,
  disabled = false,
  className,
}) => {
  const isOn = checked || indeterminate;

  return (
    <span
      aria-hidden
      className={classNames(
        "relative w-4 h-4 rounded-cds-4 border-[1px] shrink-0",
        "flex items-center justify-center",
        "transition-colors duration-75",
        // Resting elevation — tracks ButtonV2 primary / secondary.
        !disabled && "shadow-cds-s",
        // On = selected or indeterminate. Both use the primary green
        // gradient + stroke; the inner glyph differentiates them.
        isOn
          ? "bg-cds-primary-fill-default border-cds-primary-stroke-default"
          : "bg-cds-surface-default border-cds-surface-border dark:bg-white/[0.12] dark:border-white/10",
        // Disabled:
        //   - selected/indeterminate → keep green fill + 50% opacity
        //   - unselected light → zinc-100 fill (reads as an inert well)
        //   - unselected dark    → translucent fill + 50% opacity
        disabled && isOn && "opacity-50",
        disabled && !isOn && "bg-cds-surface-active dark:bg-white/[0.12] dark:opacity-50",
        className,
      )}
    >
      {indeterminate ? (
        // 8×2 white bar, centered. Fully rounded so the ends read as
        // soft rather than hard-cut pixels at this tiny size.
        // `bg-[#ffffff]` (arbitrary hex) avoids dark-mode.css's
        // `.dark .bg-white → zinc-900 !important` global remap.
        <span className={"w-2 h-cds-2 rounded-full bg-[#ffffff]"} />
      ) : checked ? (
        <Icon
          // `checkMark` = plain Lucide `Check` (stroke only).
          // `check` in the registry is the encircled variant used
          // elsewhere — the Figma checkbox spec wants just the
          // glyph, no circle.
          name={"checkMark"}
          // `size="sm"` renders 12×12 (via Icon's IconSizes map),
          // leaving ~2px of padding on each side of the 16×16
          // checkbox box. Using the size prop instead of custom
          // `!w-3 !h-3` because the `size` prop also sets
          // `min-w`/`min-h` to match, which a plain `w-3` class
          // otherwise fights and loses to. `!text-white` beats
          // dark-mode.css's `.dark svg { color: inherit }`.
          size={"sm"}
          // SVG stroke scales with viewBox: Lucide Check's 0→24
          // viewBox rendered at 12px means each stroke unit ≈ 0.5
          // CSS px. `strokeWidth={3}` renders as ~1.5 CSS px,
          // closer to the 2-px-tall indeterminate bar's visual
          // weight. Go higher if it still reads thin.
          strokeWidth={3}
          className={"!text-white"}
        />
      ) : null}
    </span>
  );
};

export const CheckboxV2 = forwardRef(function CheckboxV2Impl(
  {
    size: _size = "md",
    checked = false,
    indeterminate = false,
    disabled,
    onChange,
    className,
    "data-testid": dataTestId,
    type = "button",
    ...rest
  }: CheckboxV2Props,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const amplitudeOnClick = useAmplitudeOnClick({
    event: AmplitudeEvent.DESIGN_SYSTEM_CHECKBOX_CLICK,
    component: "CheckboxV2",
    text: rest["aria-label"],
    extraProperties: {
      color: checked || indeterminate ? "primary" : "secondary",
      data_test_id: dataTestId,
    },
  });

  return (
    <button
      ref={ref}
      type={type}
      role={"checkbox"}
      aria-checked={indeterminate ? "mixed" : checked}
      disabled={disabled}
      data-testid={dataTestId}
      onClick={() => {
        amplitudeOnClick();
        onChange?.(!checked);
      }}
      data-ds-v2={""}
      className={classNames(
        "inline-flex h-4 w-4 items-center justify-center rounded-cds-4 border-0 bg-transparent p-0",
        "align-top focus:outline-none focus:shadow-cds-focus",
        disabled && "cursor-not-allowed",
        className,
      )}
      {...rest}
    >
      <CheckboxV2Control checked={checked} indeterminate={indeterminate} disabled={disabled} />
    </button>
  );
});
